import { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useGet, usePost, useSubmit, useDelete, API, Card, Date, Help, InfoBox, Input, Output, Submit, ViewHeader } from '@enkla-gui'

import { date, invoicebasehelper, notify } from '@/_helpers'
import { billable } from '@/_config'

import Events from '@components/tools/Events'
import InvoiceRows from '@components/invoicebase/InvoiceRows'
import Modal from 'react-modal'

export default function InvoicebaseEdit() {
  const { id } = useParams()
  const history = useHistory()

  const [downloading, setDownloading] = useState(false)
  const [generateLoading, setGenerateLoading] = useState(false)
  const [generateModal, setGenerateModal] = useState(false)
  const [dueAtModal, setDueAtModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState(false)
  const [refreshToken, setRefreshToken] = useState(false)

  const { data: invoicebase, fetch: getInvoicebase, loading } = useGet({
    endpoint: `api/invoicebase/${id}`,
    onSuccess: () => setRefreshToken(date.now())
  })

  const { remove, loading: deleting } = useDelete({
    endpoint: `api/invoicebase/${id}`,
    confirm: 'Vill du radera denna hyresavi?',
    success: () => history.push(`/lease/${invoicebase?.lease?.id}`)
  })

  const { post: cancel, loading: cancelling } = usePost({
    confirm: 'Vill du makulera denna hyresavi?',
    success: getInvoicebase
  })

  const { post: credit, loading: crediting } = usePost({
    confirm: 'Vill du kreditera denna hyresavi?',
    success: getInvoicebase
  })

  const { post: mail, loading: mailing } = usePost({
    confirm: 'Vill du skicka hyresavin till kund via e-post?',
    success: getInvoicebase
  })

  const { post: einvoice, loading: einvoicing } = usePost({
    confirm: 'Vill du skicka hyresavin som e-faktura?',
    success: getInvoicebase
  })

  const { post: bookkeep, loading: bookkeeping } = usePost({
    confirm: 'Vill du bokföra hyresavin?',
    success: getInvoicebase
  })

  const { handleSubmit, control, submit: changeDueAt, loading: dueAtLoading, error: dueAtError, setValue } = useSubmit({
    endpoint: `api/invoicebase/${id}/dueat`,
    method: 'patch',
    success: () => {
      getInvoicebase()
      setDueAtModal(false)
    }
  })

  useEffect(() => {
    if (dueAtModal && invoicebase?.due_at) {
      setValue('due_at', invoicebase.due_at)
    }
  }, [dueAtModal, invoicebase?.due_at, setValue])

  useEffect(() => {
    if (invoicebase?.message) {
      notify.toast(invoicebase.message, 'error')
    }
  }, [invoicebase?.message])

const { register: genRegister, handleSubmit: genHandleSubmit, error: generateError, loading: generating, setValue: setGenValue } = useSubmit({})

  const { post: markReady, loading: readying } = useSubmit({
    endpoint: 'api/invoicebase',
    method: 'patch',
    confirm: 'Vill du markera underlaget som redo att skickas?',
    success: getInvoicebase
  })

  const { post: markAsSent, loading: markingSent } = usePost({
    confirm: 'Vill du ändra status till skickad?',
    success: getInvoicebase
  })

  const { register: payRegister, handleSubmit: payHandleSubmit, submit: pay, loading: paying, error: payError } = useSubmit({
    endpoint: `api/invoicebase/${id}/pay`,
    method: 'post',
    success: () => {
      getInvoicebase()
      setPaymentModal(false)
    }
  })

const generate = (data) => {
  setGenerateLoading(true)
  API.post(`api/invoicebase/${id}/generate`, {
    ...data
  })
    .then(res => {
      notify.toast(res.message || 'Hyresavi skapad')
      getInvoicebase()
      setGenerateModal(false)
      setGenerateLoading(false)
    })
    .catch(([err, res]) => {
      notify.alert('Hyresavin kunde inte skapas', res.message, 'error')
      setGenerateLoading(false)
    })
}

  const preview = async () => {
    setDownloading(true)
    API.post(`api/invoicebase/${id}/preview`)
      .then(res => {
        const linkSource = `data:application/pdf;base64,${res.file}`
        const downloadLink = document.createElement('a')
        const fileName = `Hyresavi_${invoicebase?.reference}.pdf`
        downloadLink.href = linkSource
        downloadLink.download = fileName
        downloadLink.click()
        setDownloading(false)
      })
      .catch(([err, res]) => {
        notify.alert('Något gick fel', res.message, 'error')
        setDownloading(false)
      })
  }

  const download = () => {
    API.get(`api/file/${invoicebase?.file?.id}`, {})
      .then(res => {
        var link = document.createElement('a')
        link.href = res
        link.target = '_blank'
        link.click()
      })
      .catch(([err, res]) => {
        notify.toast(res.message, 'error')
      })
  }

  const renderButtons = () => {
    const isEdit = invoicebase?.status === 'EDIT'
    const isManual = invoicebase?.invoice_type === 'MANUAL'
    return [
      {
        title: 'Skicka',
        hidden: isEdit || !['READY', 'PENDING_PAYMENT', 'OVERDUE', 'PARTIALLY_PAYED', 'PAYED'].includes(invoicebase?.status),
        dropdown: true,
        icon: 'fas fa-paper-plane',
        loading: mailing || einvoicing || markingSent,
        options: [
          { title: 'Skicka som e-post', icon: 'fas fa-at', callback: () => mail({ endpoint: `api/invoicebase/${invoicebase?.id}/mail` }) },
          ...(!isManual ? [{ hidden: isManual, title: 'Skicka som e-faktura', icon: 'fas fa-file-invoice', callback: () => einvoice({ endpoint: `api/invoicebase/${invoicebase?.id}/einvoice` }) }] : []),
          ...(isManual ? [{ title: 'Ändra status till skickad (skickas via post)', icon: 'fas fa-envelope', callback: () => markAsSent({ endpoint: `api/invoicebase/${invoicebase?.id}/sent` }) }] : [])
        ]
      },
      { title: 'Bokför', icon: 'fas fa-file-invoice', hidden: isEdit, loading: bookkeeping, callback: () => bookkeep({ endpoint: `api/invoicebase/${invoicebase?.id}/bookkeep` }) },
      { title: 'Kreditera', icon: 'fas fa-credit-card', hidden: isEdit, loading: crediting, callback: () => credit({ endpoint: `api/invoicebase/${invoicebase?.id}/credit` }) },
      { title: 'Makulera', icon: 'fas fa-ban', hidden: isEdit, loading: cancelling, callback: () => cancel({ endpoint: `api/invoicebase/${invoicebase?.id}/cancel` }) },
      {
        title: 'Justera förfallodatum',
        icon: 'fas fa-calendar',
        hidden: isEdit || ['EDIT', 'CANCELLED', 'CREDITED'].includes(invoicebase?.status),
        callback: () => setDueAtModal(true)
      },
      ...(isManual ? [{ title: 'Registrera betalning', icon: 'fas fa-money-bill', callback: () => setPaymentModal(true) }] : []),
      { loading: downloading, title: 'Förhandsgranska', icon: 'fas fa-file-pdf', callback: () => preview(), hidden: isEdit || invoicebase?.file },
      { loading: downloading, title: 'Ladda ner hyresavi (PDF)', icon: 'fas fa-file-pdf', callback: () => download(), hidden: isEdit || !invoicebase?.file }
    ]
  }

  return (
    <>
      <ViewHeader
        loading={loading}
        subtitle={invoicebase?.title}
        icon={invoicebase?.invoice_type === null ? 'fa-pen-square' : 'fa-file-invoice'}
        title="Hyresavi"
        breadcrumbs={invoicebase?.breadcrumb}
        badges={[{ bg: invoicebasehelper.status(invoicebase?.status)?.color, text: invoicebasehelper.status(invoicebase?.status)?.label }]}
        buttons={[
          {
            title: 'Generera hyresavi',
            icon: 'fa-plus',
            hidden: invoicebase?.invoice_type !== null,
            dropdown: true,
            loading: generating || generateLoading,
            options: [
              {
                title: 'Skapa manuell hyresavi',
                icon: 'fa-plus',
                callback: () => {
                  setGenValue('integration_id', null)
                  setGenerateModal(true)
                }
              },
              ...(invoicebase?.lease?.company?.invoicingconfig?.integration ? [{
                title: `Skapa hyresavi i ${invoicebase.lease.company.invoicingconfig.invoice_method}`,
                icon: 'fa-file-invoice',
                callback: () => {
                  setGenValue('integration_id', invoicebase.lease.company.invoicingconfig.integration.id)
                  genHandleSubmit(generate)()
                }
              }] : [])
            ]
          },
          {
            position: 'right',
            icon: 'fa-trash',
            title: 'Radera',
            loading: deleting,
            callback: () => remove(),
          },
          {
            hidden: !['REVIEW', 'ERROR'].includes(invoicebase?.status) || invoicebase?.invoice_type === null,
            icon: 'fa-check',
            title: 'Markera som redo att skickas',
            loading: readying,
            callback: () => markReady({ ...invoicebase, status: 'READY' }),
            color: 'primary'
          }
        ].concat(renderButtons())}
      />
      {invoicebase?.invoice_type !== null && (
        <div class='row'>
          <InfoBox
            loading={loading}
            title='Skickad till kund'
            subtitle={invoicebase?.sent_at === null ? 'Ej skickad' : date.full(invoicebase?.sent_at)}
            icon='fa-paper-plane'
          />
          <InfoBox
            loading={loading}
            title='Hyresavi bokförd'
            subtitle={invoicebase?.booked_at === null ? 'Ej bokförd' : date.full(invoicebase?.booked_at)}
            icon='fa-book'
          />
        </div>
      )}
      <Card loading={loading} title='Information' icon='fas fa-info-circle'>

        <div class='row'>
          <div class='col-6'>
            <div class='mb-3'>
              <Output type='currency' currency={billable.currency} value={invoicebase?.fee} label='Belopp' />
            </div>
            <div class='mb-3'>
              <Output type='currency' currency={billable.currency} value={invoicebase?.tax} label='Moms' />
            </div>
            <div class='mb-3'>
              <Output type='currency' currency={billable.currency} value={invoicebase?.rounded_amount} label='Öresavrundning' />
            </div>
            <div class='mb-3'>
              <Output type='currency' currency={billable.currency} value={invoicebase?.pay_amount_rounded} label='Att betala' />
            </div>
            {invoicebase?.payment_left !== null && (
              <div class='mb-3'>
                <Output
                  type='currency'
                  value={invoicebase?.payment_left}
                  bg={invoicebase?.payment_left <= 0 ? 'success' : 'error'}
                  label='Kvar att betala'
                />
              </div>
            )}
          </div>
          <div class='col-6 '>
            <div class='mb-3'>
              <Output value={date.short(invoicebase?.start) + ' - ' + date.short(invoicebase?.stop)} label='Avser Period' />
            </div>
            <div class='mb-3'>
              <Output value={invoicebase?.due_at ? date.short(invoicebase.due_at) : 'Hyresavi ej genererad'} label='Förfaller' />
            </div>
            {invoicebase?.invoice_type !== null && (
              <div class='mb-3'>
                <div class='row mb-3'>
                  <Output value={invoicebase?.reference} label='Referens' />
                  {invoicebase?.ocr !== null && <Output value={invoicebase?.ocr} label='OCR' />}
                </div>
              </div>
            )}
            {invoicebase?.autogiro && (
              <div class='mb-3'>
                <div class='row mb-3'>
                  <Output value={'Autogiro'} label='Betalsätt' />
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      <div class='row'>
    {invoicebase != null && <InvoiceRows fetch={() => getInvoicebase()} invoicebase={invoicebase} />}
      </div>
      {invoicebase?.tenant && (
        <Card
          loading={loading}
          title='Fakturerad part'
          icon='fas fa-address-book'
          buttons={{
            header: [{ text: 'Gå till hyresgäst', icon: 'fas fa-address-book', link: `#!/tenant/${invoicebase?.tenant?.id}` }]
          }}>
          <div class='row mb-3'>
            {(() => {
              const ic = invoicebase?.tenant?.integration_customers?.find(
                c => c.integration.id === invoicebase?.integration?.id
              )
              const label = ic ? `Kundnummer (${invoicebase?.integration?.nice_name})` : null
              return ic
                ? <Output value={ic.customer_number} label={label} />
                : null
            })()}
            <Output value={invoicebase.tenant.name} label='Namn' />
          </div>
          <div class='row mb-3'>
            <Output value={invoicebase.tenant.email} label='E-post' />
            <Output value={invoicebase.tenant.phone} label='Telefonnummer' />
          </div>
        </Card>
      )}
      <div class='row'>
        <div class='col'>
          <Events id={invoicebase?.id} type='invoicebase' refresh={refreshToken}/>
        </div>
      </div>
      <Modal isOpen={generateModal} onRequestClose={() => setGenerateModal(false)} className='no-padding'>
        <Card
          collapsed={null}
          title='Skapa hyresavi'
          icon='fas fa-file-invoice'
          buttons={{
            header: [{ callback: () => setGenerateModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={genHandleSubmit(generate)} text='Skapa' sending={generating || generateLoading} />]
          }}>
          <div class='row mb-3'>
            <Input controls={{ register: genRegister }} name='reference' placeholder='Om inget värde anges genereras ett fakturanummer' label='Fakturanummer' error={generateError} />
          </div>
          <div class='row mb-3'>
            <Input controls={{ register: genRegister }} name='ocr' label='OCR' error={generateError} />
          </div>
          {generateError &&
            <Help header={false} type='danger'>
              <div class="help" dangerouslySetInnerHTML={{ __html: generateError.message }} />
            </Help>
          }
        </Card>
      </Modal>
      <Modal isOpen={dueAtModal} onRequestClose={() => setDueAtModal(false)} className='no-padding'>
        <Card
          title='Redigera Förfallodatum'
          collapsed={null}
          icon='fas fa-fw fa-calendar'
          buttons={{
            header: [{ callback: () => setDueAtModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={handleSubmit(changeDueAt)} text='Uppdatera' sending={dueAtLoading} />]
          }}>
          {invoicebase?.booked_at !== null && invoicebase?.autogiro &&
            <Help>
              <p> Om du ändrar förfallodatum på en hyresavi med autogiro påverkas inte den dag då beloppet dras. </p>
            </Help>
          }
          <div class='row mb-3'>
            <Date label='Förfallodatum' controls={{ control }} name='due_at' error={dueAtError} options={{ required: true }} />
          </div>
        </Card>
      </Modal>
      <Modal isOpen={paymentModal} onRequestClose={() => setPaymentModal(false)} className='no-padding'>
        <Card
          title='Registrera betalning'
          collapsed={null}
          icon='fas fa-fw fa-money-bill'
          buttons={{
            header: [{ callback: () => setPaymentModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
            footer: [<Submit onSubmit={payHandleSubmit(pay)} text='Spara' sending={paying} />]
          }}>
          <div class='row mb-3'>
            <Input controls={{ register: payRegister }} name='amount' options={{ required: true }} label='Belopp' type='number' error={payError} />
          </div>
        </Card>
      </Modal>
    </>
  )
}
